<!-- Adapted from SPlayer 40d9ec6 src/views/Discover/layout.vue (AGPL-3.0).
     Vue 2 adapter: shared catalog/account services replace SPlayer stores. -->
<template>
  <section v-if="view === 'cloud'" class="splayer-online splayer-cloud" aria-label="我的云盘">
    <NeteaseCloudPanel />
  </section>
  <section v-else class="splayer-online discover" :class="{'mobile-layout':navigationUI.mobile}" aria-label="在线音乐">
    <header class="discover-header">
      <div ref="onlineTabs" class="tabs" role="tablist" aria-label="在线音乐分类" @keydown="moveTab">
        <SaltButton v-for="tab in tabs" :id="'online-tab-' + tab.id" :key="tab.id" :style-type="activeTab === tab.id ? 'subtle' : 'plain'" role="tab"
          :data-online-tab="tab.id" :aria-selected="activeTab === tab.id" :tabindex="activeTab === tab.id ? 0 : -1"
          aria-controls="online-tab-panel" :class="{ active: activeTab === tab.id }" @click="selectTab(tab.id)">{{ tab.label }}</SaltButton>
      </div>
      <div v-if="!navigationUI.mobile || exploreOwnerMode !== 'online'" class="catalog-platform" role="group" aria-label="内容平台"><SaltButton v-for="platform in platforms" :key="platform.id" :style-type="catalogSettings.platform === platform.id ? 'subtle' : 'plain'" :aria-pressed="catalogSettings.platform === platform.id" @click="selectPlatform(platform.id)">{{ platform.name }}</SaltButton></div>
    </header>
    <div id="online-tab-panel" role="tabpanel" :aria-labelledby="'online-tab-' + activeTab">
      <component :is="activeComponent" :key="activeTab + ':' + catalogSettings.platform" class="router-view" />
    </div>
  </section>
</template>

<script>
import { runtime } from '../bridge';
import SaltButton from './ui/SaltButton.vue';
import NeteaseCloudPanel from '../components/NeteaseCloudPanel.vue';
import DiscoverPlaylists from './online/DiscoverPlaylists.vue';
import DiscoverToplists from './online/DiscoverToplists.vue';
import DiscoverArtists from './online/DiscoverArtists.vue';
import DiscoverNew from './online/DiscoverNew.vue';
import { pushOnlineQuery } from './online/catalog-feed';
import { workspace, navigationUI } from '../workspace';
import { readWorkspacePageState, patchWorkspacePageState } from '../workspace-view-state';

const tabs = [
  { id: 'playlists', label: '歌单广场', component: DiscoverPlaylists },
  { id: 'toplists', label: '排行榜', component: DiscoverToplists },
  { id: 'artists', label: '歌手', component: DiscoverArtists },
  { id: 'new', label: '最新音乐', component: DiscoverNew },
];

export default {
  name: 'SPlayerExplore',
  components: { SaltButton, NeteaseCloudPanel },
  props: { view: { type: String, default: 'discover' } },
  data() { const exploreOwnerMode = workspace.mode; const saved = readWorkspacePageState(exploreOwnerMode, 'explore'); if (saved.platform) runtime.setCatalogPlatform(saved.platform); return { tabs, navigationUI, catalogSettings: runtime.catalogSettings, exploreOwnerMode, platforms: [{ id: 'wy', name: '网易云音乐' }, { id: 'tx', name: 'QQ音乐' }] }; },
  computed: {
    activeTab() {
      const value = this.$route.query.onlineTab;
      if (tabs.some(tab => tab.id === value)) return value;
      return this.$route.query.category === '排行榜' ? 'toplists' : 'playlists';
    },
    activeComponent() { return tabs.find(tab => tab.id === this.activeTab).component; },
  },
  watch: {
    'catalogSettings.platform'() { this.persistExplorePlatform(); },
    activeTab() { this.$nextTick(this.revealActiveTab); },
    view() { this.$nextTick(this.observeTabs); },
  },
  created() { this.persistExplorePlatform(); },
  mounted() { this.observeTabs(); },
  beforeDestroy() { this._tabsResize?.disconnect(); },
  methods: {
    observeTabs() {
      this._tabsResize?.disconnect();
      if (!this.$refs.onlineTabs) return;
      if (typeof ResizeObserver !== 'undefined') {
        this._tabsResize = new ResizeObserver(this.revealActiveTab);
        this._tabsResize.observe(this.$refs.onlineTabs);
      }
      this.revealActiveTab();
    },
    revealActiveTab() {
      const nav = this.$refs.onlineTabs, active = nav?.querySelector('[data-online-tab="' + this.activeTab + '"]');
      if (!active) return;
      const main = this.$el.closest('main'), mainTop = main?.scrollTop;
      const bounds = nav.getBoundingClientRect(), selected = active.getBoundingClientRect();
      // Scroll only the horizontal tab strip; preserve the page's saved feed position.
      if (selected.left < bounds.left) nav.scrollLeft += selected.left - bounds.left;
      else if (selected.right > bounds.right) nav.scrollLeft += selected.right - bounds.right;
      if (main && Number.isFinite(mainTop) && Math.abs(main.scrollTop - mainTop) > 0.5) main.scrollTop = mainTop;
    },
    persistExplorePlatform() { if (workspace.mode === this.exploreOwnerMode) patchWorkspacePageState(this.exploreOwnerMode, 'explore', { platform: this.catalogSettings.platform }); },
    selectPlatform(platform) { runtime.setCatalogPlatform(platform); this.persistExplorePlatform(); },
    selectTab(onlineTab) { pushOnlineQuery(this, { onlineTab }); },
    moveTab(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const target = event.target.closest('[data-online-tab]');
      if (!target) return;
      event.preventDefault();
      const index = tabs.findIndex(tab => tab.id === target.dataset.onlineTab);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      this.selectTab(tabs[next].id);
      this.$nextTick(() => {
        this.$el.querySelector('[data-online-tab="' + tabs[next].id + '"]').focus({ preventScroll: true });
        this.revealActiveTab();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.splayer-online { min-width: 0; color: var(--salt-text, var(--color-text)); }
.discover { container-type:inline-size; display: flex; flex-direction: column; }
.discover-header { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:8px 16px; margin-bottom:16px; }
.tabs { display:flex; gap:4px; min-width:0; max-width:100%; overflow-x:auto; overscroll-behavior-x:contain; scrollbar-width:thin; scrollbar-color:var(--salt-stroke,var(--salt-border,#8882)) transparent; }
.tabs button { flex:1 0 auto; color:var(--salt-text,var(--color-text)); }
.tabs button.active { color:var(--salt-accent,var(--color-primary)); }
button:focus:not(:focus-visible) { outline: none; }
button:focus-visible { outline: 2px solid var(--salt-accent, var(--color-primary)); outline-offset: -2px; }
.catalog-platform { display:flex; align-items:center; flex-wrap:wrap; gap:4px; }
.catalog-platform button { color:var(--salt-text,var(--color-text)); }
.catalog-platform button[aria-pressed='true'] { color:var(--salt-accent,var(--color-primary)); }
.splayer-cloud ::v-deep .cloud-heading { margin-bottom: 16px; }
.splayer-cloud ::v-deep .cloud-heading h2 { font-size: 24px; font-weight: 500; }
.splayer-cloud ::v-deep .cloud-heading button { min-height:32px; padding:6px 12px; border:0; border-radius:999px; background:color-mix(in srgb,var(--salt-text,var(--color-text)) 8%,transparent); color:inherit; font-size:14px; }
.splayer-cloud ::v-deep .track { border-radius: 4px; }
@container (max-width:560px) { .discover-header { gap:8px; } .tabs { width:100%; } }
.mobile-layout { .discover-header { margin-top:4px; } .catalog-platform { width:100%; } .catalog-platform button { flex:1; min-height:44px; } .tabs { order:2; width:100%; padding-top:4px; border-top:1px solid var(--salt-stroke,var(--salt-border,#8882)); } .tabs button { min-height:44px; } }
@media (min-width:600px) and (max-width:1023px) and (max-height:599px) {
  .catalog-platform button, .tabs button { min-height:44px; }
}
</style>
